/**
 * Big, glanceable check-in outcome for ushers: green = pass, amber = needs a
 * decision, red = stop.
 */

import { AlertTriangle, CheckCircle2, Printer, XCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import type { CheckinResult, CheckinWarning } from '../checkinRepository';

const WARNING_TEXT: Record<CheckinWarning, string> = {
  NOT_VERIFIED: 'Inscripción no verificada (documentos / pago pendientes de revisión)',
  UNPAID: 'Pago pendiente',
};

const KIND_LABEL = { MINISTER: 'Ministro', DELEGATE: 'Delegado', GUEST: 'Invitado' } as const;

function time(iso?: string) {
  return iso ? new Date(iso).toLocaleTimeString('es-BO', { hour: '2-digit', minute: '2-digit' }) : '';
}

interface CheckinResultCardProps {
  result: CheckinResult;
  onConfirm?: () => void;
  onDismiss: () => void;
  onPrintSticker?: () => void;
  busy?: boolean;
}

export function CheckinResultCard({ result, onConfirm, onDismiss, onPrintSticker, busy }: CheckinResultCardProps) {
  const p = result.participant;

  const tone =
    result.result === 'OK'
      ? { bg: 'bg-green-600', icon: CheckCircle2, title: 'Ingreso registrado' }
      : result.result === 'ALREADY'
        ? { bg: 'bg-blue-600', icon: CheckCircle2, title: `Ya registrado a las ${time(result.checked_in_at)}` }
        : result.result === 'NEEDS_CONFIRMATION'
          ? { bg: 'bg-amber-500', icon: AlertTriangle, title: 'Requiere confirmación' }
          : {
              bg: 'bg-red-600',
              icon: XCircle,
              title:
                result.result === 'REJECTED'
                  ? 'Inscripción rechazada'
                  : result.result === 'NOT_FOUND'
                    ? 'Código no válido'
                    : result.result === 'WRONG_EVENT'
                      ? 'Credencial de otro evento'
                      : 'Evento no activo',
            };
  const Icon = tone.icon;

  return (
    <div className="overflow-hidden rounded-xl border shadow-lg">
      <div className={`flex items-center gap-3 p-4 text-white ${tone.bg}`}>
        <Icon className="size-10 shrink-0" />
        <p className="text-xl font-bold leading-tight">{tone.title}</p>
      </div>

      <div className="space-y-3 bg-card p-4">
        {p ? (
          <div>
            <p className="text-2xl font-semibold leading-tight">{p.full_name}</p>
            <p className="text-sm text-muted-foreground">
              {KIND_LABEL[p.kind]}
              {p.adb_code && ` · ${p.adb_code}`}
              {p.dni && ` · CI ${p.dni}`}
              {p.district_name && ` · ${p.district_name}`}
            </p>
          </div>
        ) : (
          <p className="text-sm text-muted-foreground">El código escaneado no corresponde a una inscripción.</p>
        )}

        {result.result === 'REJECTED' && result.rejection_reason && (
          <p className="text-sm text-red-700">Motivo: {result.rejection_reason}</p>
        )}

        {result.warnings && result.warnings.length > 0 && (
          <ul className="space-y-1 text-sm text-amber-800">
            {result.warnings.map((w) => (
              <li key={w} className="flex items-start gap-2">
                <AlertTriangle className="mt-0.5 size-4 shrink-0" /> {WARNING_TEXT[w]}
              </li>
            ))}
          </ul>
        )}

        <div className="flex flex-wrap gap-2">
          {result.result === 'NEEDS_CONFIRMATION' && onConfirm && (
            <Button size="lg" className="flex-1" onClick={onConfirm} disabled={busy}>
              Registrar igual
            </Button>
          )}
          {onPrintSticker && p && p.registration_status === 'VERIFIED' && (
            <Button size="lg" variant="outline" onClick={onPrintSticker}>
              <Printer className="mr-2 size-4" /> Sticker
            </Button>
          )}
          <Button size="lg" variant={result.result === 'NEEDS_CONFIRMATION' ? 'outline' : 'default'} className="flex-1" onClick={onDismiss}>
            {result.result === 'NEEDS_CONFIRMATION' ? 'Enviar a mesa de registro' : 'Siguiente'}
          </Button>
        </div>
      </div>
    </div>
  );
}
